import { CommonModule } from "@angular/common";
import {
    AfterViewChecked,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    inject,
} from "@angular/core";
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from "@angular/forms";

import { IncidentTypesService } from "../../incident/services/incident-types.service";
import { IncidentTypeEditModel } from "../../incident/models/incident-type-edit.model";
import { MatButtonModule } from "@angular/material/button";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    CdkDropListGroup,
} from "@angular/cdk/drag-drop";
import { IncidentTypeSectionModel } from "@app/incident/models/incident-type-section.model";
import { MatMenuModule } from "@angular/material/menu";
import { CustomFieldModel } from "@app/incident/models/custom-field.model";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { CustomFieldConfigComponent } from "../custom-field-config/custom-field-config.component";
import { ToastrService } from "ngx-toastr";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FieldType } from "@app/preset/models/field-type.enum";
import { BaseStandingDataEditComponent } from "../interfaces/base-standing-data-edit.component";
import { MatTabsModule } from "@angular/material/tabs";
import { ConfigurationTabSettingsModel } from "../models/configuration-tab-settings.model";
import { CerrixTitleComponent } from "@app/incident/components/src/lib/core/cerrix-title/cerrix-title.component";
import { CerrixButtonComponent } from "@app/incident/components/src/lib/core/cerrix-button/cerrix-button.component";
import { CerrixInputComponent } from "@app/incident/components/src/lib/core/cerrix-input/cerrix-input.component";
import { CerrixTextareaComponent } from "@app/incident/components/src/lib/core/cerrix-textarea/cerrix-textarea.component";
import { CerrixFieldWrapperComponent } from "@app/incident/components/src/lib/core/cerrix-field-wrapper/cerrix-field-wrapper.component";
import { CerrixToggleComponent } from "@app/incident/components/src/lib/core/cerrix-toggle/cerrix-toggle.component";
import { CerrixDialogService } from "@app/incident/components/src/lib/core/services/cerrix-dialog.service";
import { CerrixValidators } from "@app/incident/components/src/lib/core/validation/CerrixValidators";
import { FormsDataService } from "@app/forms/services/forms-data.service";

@Component({
    selector: "incident-type-edit",
    templateUrl: "./incident-type-edit.component.html",
    styleUrls: ["./incident-type-edit.component.scss"],
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        CerrixTitleComponent,
        CerrixButtonComponent,
        MatGridListModule,
        MatCardModule,
        CerrixInputComponent,
        CerrixTextareaComponent,
        CerrixFieldWrapperComponent,
        CdkDrag,
        CdkDragHandle,
        CdkDropList,
        CdkDropListGroup,
        MatMenuModule,
        MatDialogModule,
        MatTabsModule,
        CerrixToggleComponent,
    ],
    providers: [],
})
export class IncidentTypeEditComponent
    implements OnInit, AfterViewChecked, BaseStandingDataEditComponent
{
    @Input() public id: string;

    @Output() public onEntityUpdated = new EventEmitter<string>();

    @ViewChildren("section") sections: QueryList<ElementRef>;

    private readonly dialog = inject(MatDialog);
    private readonly incidentTypesService = inject(IncidentTypesService);
    private readonly formService = inject(FormsDataService);
    private readonly toastrService = inject(ToastrService);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly formBuilder: FormBuilder = inject(FormBuilder);
    private dialogService = inject(CerrixDialogService);

    private addedSection: boolean = false;

    protected form: FormGroup = undefined;
    protected predefined: boolean = true;
    protected readonly fullWidthCustomTypes: FieldType[] = [
        FieldType.TextBox,
        FieldType.FinancialImpact,
    ];

    protected configurationTabSettings: ConfigurationTabSettingsModel = {
        assign: {
            showSection: true,
            mailAddressesToInform: true,
        },
        configuration: {
            showSection: true,
            archiveIncidentType: true,
        },
    };

    public get hasChanges(): boolean {
        return this.form.dirty;
    }

    protected get sectionsForm(): FormArray {
        return this.form.get("incidentTypeSections") as AbstractControl as FormArray;
    }

    protected get detailCustomFieldsForm(): FormArray {
        return this.form.get("customFields") as AbstractControl as FormArray;
    }

    public ngOnInit() {
        this.loadIncidentType();
        Object.entries(this.configurationTabSettings).forEach(([key, value]) => {
            this.configurationTabSettings[key].showSection = Object.entries(value).some(
                ([sectionKey, sectionValue]) => sectionKey !== "showSection" && sectionValue,
            );
        });
    }

    public ngAfterViewChecked(): void {
        if (this.addedSection) {
            this.addedSection = false;
            this.sections.last.nativeElement.scrollIntoView({ behavior: "smooth" });
            const nameField = this.sections.last.nativeElement.querySelector(
                "cerrix-title .display-title",
            );
            setTimeout(() => nameField.click(), 500);
        }
    }

    protected loading: boolean = false;

    protected showSavedButton = false;
    private savedButtonTimeout = undefined;

    protected onSave() {
        if (this.hasChanges) {
            this.validateAllFormFields(this.form);

            if (this.form.valid) {
                this.loading = true;

                if (this.form.value.id) {
                    this.incidentTypesService
                        .editIncidentType(this.getFormValues())
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: () => {
                                this.loading = false;

                                this.onEntityUpdated.emit(this.form.value.id);
                                this.loadIncidentType();

                                this.displaySavedMessages();
                            },
                            error: () => {
                                this.loading = false;
                            },
                        });
                } else {
                    this.incidentTypesService
                        .addIncidentType(this.getFormValues())
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (id) => {
                                this.loading = false;

                                this.id = id;
                                this.onEntityUpdated.emit(id);
                                this.loadIncidentType();

                                this.displaySavedMessages();
                            },
                            error: () => {
                                this.loading = false;
                            },
                        });
                }
            }
        }
    }

    protected onDelete() {
        this.loading = true;
        this.incidentTypesService
            .deleteIncidentType(this.form.value.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.loading = false;

                    this.toastMessage("Deleted");

                    this.onEntityUpdated.emit(null);
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    private displaySavedMessages() {
        this.showSavedButton = true;
        this.savedButtonTimeout = setTimeout(() => {
            this.savedButtonTimeout = undefined;
            this.showSavedButton = false;
        }, 2500);

        this.toastMessage("Saved");
    }

    private toastMessage(message: string) {
        this.toastrService.success(
            `<span class="toast-html-message">${message}<span class="material-symbols-outlined">check</span></span>`,
            undefined,
            {
                enableHtml: true,
            },
        );
    }

    private loadIncidentType() {
        if (this.savedButtonTimeout) {
            clearTimeout(this.savedButtonTimeout);
            this.showSavedButton = false;
        }

        this.resetForm();

        if (this.id) {
            this.loading = true;

            this.incidentTypesService
                .getIncidentType(this.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((incidentType) => {
                    this.predefined = incidentType.isPredefined;
                    this.configurationTabSettings.configuration.archiveIncidentType = true;

                    this.form.patchValue(
                        {
                            id: incidentType.id,
                            name: incidentType.name,
                            emailAddresses: incidentType.emailAddresses,
                            isArchived: incidentType.isArchived,
                        },
                        { emitEvent: false },
                    );

                    incidentType.customFields?.forEach((cf) =>
                        this.detailCustomFieldsForm.push(this.getCustomFieldFormGroup(cf)),
                    );
                    incidentType.incidentTypeSections.forEach((its) => this.addSection(its));

                    this.loading = false;
                });
        } else {
            this.predefined = false;
            this.configurationTabSettings.configuration.archiveIncidentType = false;
        }
    }

    private validateAllFormFields(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach((field) => {
            formGroup.get(field)?.markAsTouched({ onlySelf: true });
        });
    }

    private getFormValues(): IncidentTypeEditModel {
        return {
            id: this.form.value.id,
            name: this.form.value.name,
            isPredefined: false,
            emailAddresses: this.form.value.emailAddresses,
            customFields: this.setCustomFieldsOrder(this.form),
            incidentTypeSections: this.setSectionsOrder(),
            isArchived: this.form.value.isArchived,
        };
    }

    protected addSection(
        incidentTypeSectionModel: IncidentTypeSectionModel | undefined = undefined,
    ): void {
        let order = this.sectionsForm.controls.length + 1;
        let name = "";
        let id = undefined;

        if (incidentTypeSectionModel !== undefined) {
            id = incidentTypeSectionModel.id;
            name = incidentTypeSectionModel.name;
            order = incidentTypeSectionModel.order;
        }

        this.sectionsForm.push(this.getSectionFormGroup(incidentTypeSectionModel, order), {
            emitEvent: incidentTypeSectionModel === undefined,
        });

        if (incidentTypeSectionModel === undefined) {
            this.addedSection = true;
        }
    }

    private getSectionFormGroup(
        incidentTypeSectionModel: IncidentTypeSectionModel | undefined = undefined,
        defaultOrder: number,
    ): FormGroup {
        let sectionForm = this.formBuilder.group({
            id: [incidentTypeSectionModel?.id ?? undefined],
            name: [incidentTypeSectionModel?.name ?? "", Validators.required],
            order: [incidentTypeSectionModel?.order ?? defaultOrder, Validators.required],
            isPredefined: [incidentTypeSectionModel?.isPredefined ?? false],
            customFields: this.formBuilder.array<CustomFieldModel>([]),
        });

        for (let customField of incidentTypeSectionModel?.customFields ?? []) {
            (sectionForm.get("customFields") as FormArray).push(
                this.getCustomFieldFormGroup(customField, customField.order),
            );
        }

        return sectionForm;
    }

    protected sectionReorder(event: CdkDragDrop<IncidentTypeSectionModel>): void {
        const control = this.sectionsForm.at(event.previousIndex);

        this.sectionsForm.removeAt(event.previousIndex);
        this.sectionsForm.insert(event.currentIndex, control);

        this.form.markAsDirty();
    }

    protected customFieldsReorder(event: CdkDragDrop<CustomFieldModel[]>): void {
        let control: AbstractControl;
        let sectionControl: FormArray<any>;
        let sectionIndex = event.container.id.match(/\d+|detail/);
        let previousSectionIndex = event.previousContainer.id.match(/\d+/);
        if (sectionIndex !== null) {
            if (event.previousContainer === event.container) {
                if (sectionIndex[0] === "detail") {
                    sectionControl = this.detailCustomFieldsForm;
                } else {
                    sectionControl = this.sectionsForm
                        .at(parseInt(sectionIndex[0]))
                        .get("customFields") as FormArray;
                }
                control = sectionControl.at(event.previousIndex);

                sectionControl.removeAt(event.previousIndex);
                sectionControl.insert(event.currentIndex, control);
            } else if (previousSectionIndex !== null) {
                sectionControl = this.sectionsForm
                    .at(parseInt(sectionIndex[0]))
                    .get("customFields") as FormArray;
                let previousSectionControl = this.sectionsForm
                    .at(parseInt(previousSectionIndex[0]))
                    .get("customFields") as FormArray;
                let previousControl = previousSectionControl.at(event.previousIndex);

                previousSectionControl.removeAt(event.previousIndex);
                sectionControl.insert(event.currentIndex, previousControl);
            }
            this.form.markAsDirty();
        }
    }

    protected duplicateSection(sectionFormIndex: number): void {
        const control = this.sectionsForm.at(sectionFormIndex);

        const newSection: IncidentTypeSectionModel = {
            id: undefined,
            name: control.value.name,
            order: sectionFormIndex + 1,
        };

        this.sectionsForm.insert(
            sectionFormIndex,
            this.getSectionFormGroup(newSection, sectionFormIndex),
        );
        this.sectionsForm.value.incidentTypeSections = this.setSectionsOrder();

        this.form.markAsDirty();
    }

    protected deleteSection(sectionFormIndex: number): void {
        const control = this.sectionsForm.at(sectionFormIndex);

        this.sectionsForm.removeAt(sectionFormIndex);
        this.sectionsForm.value.incidentTypeSections = this.setSectionsOrder();

        if (control.value.id !== undefined && control.value.id !== null) {
            this.form.markAsDirty();
        }
    }

    protected deleteCustomField(fieldIndex: number, sectionFormIndex?: number): void {
        let control: AbstractControl;
        let sectionControl: FormArray<any>;
        if (sectionFormIndex !== undefined && sectionFormIndex !== null) {
            sectionControl = this.sectionsForm
                .at(sectionFormIndex)
                .get("customFields") as FormArray;
            control = sectionControl.at(fieldIndex);
        } else {
            control = this.detailCustomFieldsForm.at(fieldIndex);
        }

        this.dialogService.openDialog({
            data: {
                promptUiid: "delete-customfields-dialog-uiid",
                title: `Delete custom field`,
                message: `Are you sure you want to delete ${control.value.name}?`,
                icon: "warning",
                iconClass: "material-symbols-outlined warning",
                positiveButton: {
                    text: "Delete field",
                    buttonClass: "warning",
                    onAction: () => {
                        if (sectionControl?.length > 0) {
                            sectionControl.removeAt(fieldIndex);
                        } else {
                            this.detailCustomFieldsForm.removeAt(fieldIndex);
                        }

                        if (control.value.id !== undefined && control.value.id !== null) {
                            this.form.markAsDirty();
                        }
                        return true;
                    },
                },
                negativeButton: {
                    text: "Cancel",
                    buttonClass: "cancel",
                    onAction: () => {
                        return true;
                    },
                },
            },
        });
    }

    protected editCustomField(sectionFormIndex: number | undefined, formGroup?: FormGroup): void {
        let dialogRef = this.dialog.open(CustomFieldConfigComponent, {
            width: "50%",
            height: "70%",
            data: {
                customField: formGroup?.value ?? undefined,
                isIncidentTypePredefined: formGroup?.value?.isPredefined ?? false,
            },
        });

        dialogRef.afterClosed().subscribe((editedCustomField) => {
            if (editedCustomField) {
                if (sectionFormIndex != undefined) {
                    let section = this.sectionsForm.at(sectionFormIndex) as FormGroup;
                    var customFieldForm = section.controls.customFields as FormArray;
                } else {
                    customFieldForm = this.form.controls.customFields as FormArray;
                }

                editedCustomField.order = sectionFormIndex + 1;

                if (formGroup) {
                    formGroup.patchValue({
                        name: editedCustomField.name,
                        placeholder: editedCustomField.placeholder,
                        description: editedCustomField.description,
                        customFieldType: editedCustomField.customFieldType,
                        isRequired: editedCustomField.isRequired,
                        isVisible: editedCustomField.isVisible,
                        customFieldOptions: editedCustomField.customFieldOptions,
                    });
                } else {
                    customFieldForm.push(
                        this.getCustomFieldFormGroup(editedCustomField, sectionFormIndex + 1),
                    );
                }

                this.form.markAsDirty({ emitEvent: true });
            }
        });
    }

    private getCustomFieldFormGroup(
        customField: CustomFieldModel,
        defualtOrder: number | undefined = undefined,
    ): FormGroup {
        return this.formBuilder.group({
            id: [customField.id ?? undefined],
            incidentTypeSectionId: [customField.incidentTypeSectionId ?? undefined],
            name: [customField.name ?? "", Validators.required],
            customFieldType: [customField.customFieldType, Validators.required],
            placeholder: [customField.placeholder],
            description: [customField.description],
            order: [customField.order ?? defualtOrder, Validators.required],
            isRequired: [customField.isRequired],
            isPredefined: [customField?.isPredefined ?? false],
            isVisible: [customField.isVisible],
            customFieldOptions:
                customField.customFieldType === FieldType.SingleSelect ||
                customField.customFieldType === FieldType.MultiSelect
                    ? [customField.customFieldOptions, Validators.required]
                    : null,
        });
    }

    private setCustomFieldsOrder(formGroup: FormGroup): CustomFieldModel[] {
        let customFields = formGroup.value.customFields as CustomFieldModel[];

        customFields.forEach((item: CustomFieldModel, index: number) => {
            item.order = index + 1;
        });

        return customFields;
    }

    private setSectionsOrder(): IncidentTypeSectionModel[] {
        let sections = this.form.value.incidentTypeSections;

        sections.forEach((item: IncidentTypeSectionModel, index: number) => {
            item.order = index + 1;

            item.customFields.forEach((cf: CustomFieldModel, index: number) => {
                cf.order = index + 1;
            });
        });

        return sections;
    }

    private resetForm(): void {
        this.form = this.formBuilder.group({
            id: new FormControl<string | undefined>(undefined),
            name: new FormControl<string>("", Validators.required),
            isArchived: false,
            emailAddresses: new FormControl<string>(null, {
                validators: [CerrixValidators.emailList("emailAddresses", "Invalid email address")],
                updateOn: "blur",
            }),
            customFields: this.formBuilder.array<CustomFieldModel>([]),
            incidentTypeSections: this.formBuilder.array<IncidentTypeSectionModel>([]),
        });

        this.form
            .get("isArchived")
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => this.onIsArchivedToggled(value));
    }

    private async onIsArchivedToggled(value: boolean): Promise<void> {
        if (!value || !this.predefined) {
            return;
        }

        let mapbackingForms: string[] = [];
        let message: string;

        // Check on custom field field name, since that's the only immutable known data we have
        // Anything else (like ID or Incident Type Name) can be different on any environment
        if (this.hasDateBreachOccurredCustomField()) {
            mapbackingForms = await this.formService.getMapbackingDatabreachForms();
            message =
                "There are forms with databreach mapbacking still active. Please close all active forms using databreach mapbacking first:";
        } else {
            mapbackingForms = await this.formService.getMapbackingForms();
            message =
                "There are forms with mapbacking still active. Please close all active forms using mapbacking first:";
        }

        if (!mapbackingForms.length) {
            return;
        }

        const formList = mapbackingForms.map((form) => `• ${form}`).join("\n");

        this.dialogService.openWarningConfirmDialog({
            data: {
                title: "Cannot archive incident type",
                message: `${message}\n${formList}`,
                positiveButton: {
                    text: "Ok",
                    onAction: () => true,
                },
            },
        });

        this.form.get("isArchived").setValue(false, { emitEvent: false });
        this.form.get("isArchived").markAsPristine();
    }

    private hasDateBreachOccurredCustomField(): boolean {
        const sectionCustomFields = (
            this.form.value.incidentTypeSections as IncidentTypeSectionModel[]
        ).flatMap((section) => section.customFields ?? []);

        return [...this.form.value.customFields, ...sectionCustomFields].some(
            (customField: CustomFieldModel) => customField.name === "Date breach occurred",
        );
    }
}
